import { useMemo } from 'react';
import {
  useActiveAccountLogo,
  useDateFormatPreference,
  useFormFromSubmissionId,
  useFormSubmission,
  useFormVersionFields,
  useTimeFormatPreference,
} from '@witmetrics/data-store';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/providers/AppStateProvider';
import { buildPdfFile, downloadBlob } from '@/utils/files';
import { CardListItem } from '@/components/List';
import TruncatedText from '@/components/TruncatedText';
import CustomPdf from '@/components/CustomPdf';
import FormPreviewImage from '@/components/FormPreviewImage';
import FormSubmissionMenu from '@/menus/FormSubmissionMenu';
import DeleteFormSubmissionDialog from '@/dialogs/DeleteFormSubmissionDialog';
import { classes, getUpdatedAtString, toAnswersRecord } from './utils';

export type FormSubmissionsListItemProps = {
  submissionId: number;
  onSelect: (submissionId: number) => void;
};

export default function FormSubmissionsListItem({
  submissionId,
  onSelect,
}: FormSubmissionsListItemProps) {
  const { onApiError } = useAppState();
  const logo = useActiveAccountLogo();
  const dateFormat = useDateFormatPreference();
  const timeFormat = useTimeFormatPreference();
  const form = useFormFromSubmissionId(submissionId);
  const submission = useFormSubmission(submissionId);
  const fields = useFormVersionFields(submission?.formVersionId);
  const answers = useMemo(
    () => toAnswersRecord(submission?.answers),
    [submission?.answers]
  );
  const [isDownloading, toggleDownloading] = useToggle();
  const [isDeleting, toggleDeleting] = useToggle();

  const handleDownload = async () => {
    try {
      toggleDownloading(true);
      const file = await buildPdfFile(
        form!.name,
        <CustomPdf
          logoSrc={logo!}
          form={form!}
          dateFormat={dateFormat}
          timeFormat={timeFormat}
          data={fields.map((field) => ({
            field,
            value: answers[field.id] || '',
          }))}
        />
      );
      downloadBlob(file, `${form!.name}.pdf`);
      toggleDownloading(false);
    } catch (err) {
      toggleDownloading(false);
      onApiError(err, 'Error downloading form');
    }
  };

  if (!(form && submission)) return null;

  return (
    <>
      {isDeleting && (
        <DeleteFormSubmissionDialog
          submissionId={submissionId}
          onClose={() => toggleDeleting(false)}
        />
      )}
      <CardListItem
        className={classes.auroraRow}
        renderIcon={() => (
          <FormPreviewImage
            className={classes.preview}
            submissionId={submissionId}
            versionId={submission?.formVersionId}
            answers={answers}
          />
        )}
        renderMenu={(open, ref, onClose) => (
          <FormSubmissionMenu
            anchorTo={ref}
            open={open}
            onDownload={handleDownload}
            onEdit={() => onSelect(submissionId)}
            onDelete={() => toggleDeleting(true)}
            onClose={onClose}
          />
        )}>
        <div className={classes.titleWrapper}>
          <button
            className={classes.title}
            onClick={() => onSelect(submissionId)}>
            {form.name}
          </button>
          <span className={classes.date}>
            {getUpdatedAtString(submission.updatedAt, dateFormat)}
          </span>
        </div>
        <TruncatedText className={classes.description}>
          {form.description || ''}
        </TruncatedText>
      </CardListItem>
    </>
  );
}
